import React from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { useIntl } from '@atlassian/jira-intl';
import type { Validation } from '../../../../common/types';
import messages from './messages';

type FieldsErrorProps = {
	validation: Validation | undefined;
	embedFormAsIs?: boolean;
	onEmbedFormAsIs?: () => void;
};

export const FieldsError = ({ validation, embedFormAsIs, onEmbedFormAsIs }: FieldsErrorProps) => {
	const { formatMessage } = useIntl();

	if (!validation) {
		return null;
	}

	const { unsupportedOptionalFields, unsupportedRequiredFields } = validation;
	const areThereUnsupportedFields =
		!!unsupportedOptionalFields.length || !!unsupportedRequiredFields.length;

	if (!areThereUnsupportedFields) {
		return null;
	}

	const isOptional = !!unsupportedOptionalFields.length && !unsupportedRequiredFields.length;
	const fields = isOptional ? unsupportedOptionalFields : unsupportedRequiredFields;

	if (isOptional && embedFormAsIs) {
		return null;
	}

	return (
		<Box xcss={containerStyles}>
			<SectionMessage
				title={formatMessage(
					isOptional
						? messages.optionalUnusableFieldsErrorTitle
						: messages.unusableFieldsErrorTitle,
				)}
				appearance={isOptional ? 'warning' : 'error'}
				actions={
					isOptional
						? [
								<SectionMessageAction key="1" onClick={onEmbedFormAsIs}>
									{formatMessage(messages.optionalUnusableFieldsErrorAction)}
								</SectionMessageAction>,
							]
						: undefined
				}
			>
				<p>
					{formatMessage(
						isOptional
							? messages.optionalUnusableFieldsErrorDescription
							: messages.unusableFieldsErrorDescription,
						{
							length: fields.length,
						},
					)}
				</p>
				<ul>
					{fields.map((field) => (
						<li key={field}>{field}</li>
					))}
				</ul>
				<p>
					{formatMessage(
						isOptional
							? messages.optionalUnusableFieldsErrorFixInstruction
							: messages.unusableFieldsErrorFixInstruction,
						{
							length: fields.length,
						},
					)}
				</p>
			</SectionMessage>
		</Box>
	);
};

export default FieldsError;

const containerStyles = xcss({
	marginBottom: 'space.300',
});
